import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { User } from '@app/shared/models/user.interface';
import { GlobalAuthority } from '@app/shared/models/global-authority';
import { ManagingCompanyService } from '@services/managing-company.service';
import { Observable } from 'rxjs';
import { ManagingCompany } from '@app/shared/models/building.interface';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatButton } from '@angular/material/button';
import { ManagingCompanyAuthorityType } from '@app/shared/models/managing-company-authorities.interface';
import { MatCheckbox } from '@angular/material/checkbox';

@Component({
  selector: 'app-user-dialog',
  templateUrl: './user-dialog.component.html',
  styleUrls: ['./user-dialog.component.scss'],
  imports: [
    MatDialogTitle,
    MatDialogContent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatSelect,
    MatOption,
    MatDialogActions,
    MatButton,
    MatSelectTrigger,
    MatCheckbox
  ]
})
export class UserDialogComponent implements OnInit {
  form: FormGroup;
  user: User;
  managingCompanies$: Observable<ManagingCompany[]>;
  globalAuthorities: GlobalAuthority[];
  managingCompaniesList: ManagingCompany[] = [];

  constructor(
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) user: User,
    private dialogRef: MatDialogRef<UserDialogComponent>,
    private managingCompanyService: ManagingCompanyService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.user = user;
    this.managingCompanies$ = this.managingCompanyService.getManagingCompanies();
    this.globalAuthorities = GlobalAuthority.all();

    this.form = this.fb.group({
      emailAddress: [{ value: user.emailAddress, disabled: true }],
      authMethod: [{ value: user.authMethod, disabled: true }],
      managingCompanyIds: [user.managingCompanyIds || []],
      globalAuthorities: [user.globalAuthorities]
    });
  }

  ngOnInit(): void {
    this.changeDetectorRef.markForCheck();
    this.managingCompanies$.subscribe(companies => {
      this.managingCompaniesList = companies;
      this.form.get('managingCompanyIds')?.setValue(this.user.managingCompanyIds || []);
      this.changeDetectorRef.markForCheck();
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  save(): void {
    const formVal = this.form.getRawValue();
    const selectedCompanyIds: number[] = formVal.managingCompanyIds ?? [];

    const managingCompanyAuthorities = (this.user.managingCompanyAuthorities ?? [])
      .filter((authority) => selectedCompanyIds.includes(authority.managingCompanyId))
      .filter((authority) => (authority.authorities ?? []).length > 0);

    this.dialogRef.close({
      ...this.user,
      managingCompanyIds: selectedCompanyIds,
      globalAuthorities: formVal.globalAuthorities ?? [],
      managingCompanyAuthorities
    });
  }

  getManagingCompanyLabel(): string {
    const selectedIds = this.form?.get('managingCompanyIds')?.value || [];
    return this.managingCompaniesList
      .filter(mc => selectedIds.includes(mc.id))
      .map(mc => mc.companyName)
      .join(', ');
  }

  compareById = (a: number, b: number): boolean => a === b;

  isManagingCompanyMember(managingCompanyId: number): boolean {
    return (this.form.value.managingCompanyIds ?? []).includes(managingCompanyId);
  }

  isManagingCompanyAdmin(managingCompanyId: number): boolean {
    if (!this.isManagingCompanyMember(managingCompanyId)) {
      return false;
    }

    return (this.user.managingCompanyAuthorities ?? []).some(
      (userMCAuthority) =>
        userMCAuthority.managingCompanyId === managingCompanyId &&
        (userMCAuthority.authorities ?? []).includes(ManagingCompanyAuthorityType.MANAGING_COMPANY_ADMIN)
    );
  }

  toggleAdmin(managingCompanyId: number, isChecked: boolean): void {
    const managingCompanyAuthorities = [...(this.user.managingCompanyAuthorities ?? [])];

    const hasAdminAuthorityForCompany = managingCompanyAuthorities.find(
      (authority) => authority.managingCompanyId === managingCompanyId
    );

    if (isChecked) {
      if (!hasAdminAuthorityForCompany) {
        managingCompanyAuthorities.push({
          managingCompanyId,
          authorities: [ManagingCompanyAuthorityType.MANAGING_COMPANY_ADMIN]
        });
      } else if (!(hasAdminAuthorityForCompany.authorities ?? []).includes(ManagingCompanyAuthorityType.MANAGING_COMPANY_ADMIN)) {
        hasAdminAuthorityForCompany.authorities = [
          ...(hasAdminAuthorityForCompany.authorities ?? []),
          ManagingCompanyAuthorityType.MANAGING_COMPANY_ADMIN
        ];
      }
    } else {
      if (hasAdminAuthorityForCompany) {
        hasAdminAuthorityForCompany.authorities = (hasAdminAuthorityForCompany.authorities ?? []).filter(
          (authority) => authority !== ManagingCompanyAuthorityType.MANAGING_COMPANY_ADMIN
        );

        if (hasAdminAuthorityForCompany.authorities.length === 0) {
          this.user.managingCompanyAuthorities = managingCompanyAuthorities.filter(
            (authority) => authority.managingCompanyId !== managingCompanyId
          );
          return;
        }
      }
    }

    this.user.managingCompanyAuthorities = managingCompanyAuthorities;
  }
}
